import { forwardRef, type ReactNode, type RefAttributes } from "react";
import classNames from "classnames";
import { Icon } from "../icon/Icon";
import { IconType } from "../icon/IconType";
import { type IconSize } from "../icon/IconSize";
import { resolveUrl } from "../../../utils/url/urlResolver";

export type LinkProps = JSX.IntrinsicElements["a"] &
  RefAttributes<HTMLAnchorElement> & {
    variant?: LinkType;
    disabled?: boolean;
    iconBefore?: ReactNode;
    skipResolve?: boolean;
    children?: ReactNode;
  };

export enum LinkType {
  PRIMARY = "scha__link--primary",
  SECONDARY = "scha__link--secondary",
  NAVIGATION = "scha__link--navigation",
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    variant = LinkType.PRIMARY,
    className,
    children,
    disabled,
    iconBefore: iconBeforeProp,
    href,
    skipResolve,
    ...anchorProps
  } = props;
  const cls = classNames("scha__link", variant, className, disabled && "disabled");

  const iconBefore = iconBeforeProp && (
    <span className={"scha__link__icon scha__link__icon--before"}>{iconBeforeProp}</span>
  );

  let url = undefined;
  if (href) {
    if (skipResolve) {
      url = href;
    } else {
      url = resolveUrl(href);
    }
  }

  if ("dangerouslySetInnerHTML" in anchorProps) {
    return <a className={cls} href={url} {...anchorProps} ref={ref} />;
  } else {
    return (
      <a className={cls} href={url} {...anchorProps} ref={ref}>
        {iconBefore}
        {children}
      </a>
    );
  }
});

export interface LinkButtonProps extends Omit<JSX.IntrinsicElements["button"], "dangerouslySetInnerHTML" | "ref"> {
  variant?: LinkType;
  disabled?: boolean;
  iconBefore?: ReactNode;
  children?: ReactNode;
}

/**
 * Button that uses Link component styles.
 */
export const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>((props, ref) => {
  const { variant = LinkType.PRIMARY, className, children, disabled, iconBefore: iconBeforeProp, ...btnProps } = props;
  const cls = classNames("btn-style-reset scha__link", variant, className, disabled && "disabled");

  const iconBefore = iconBeforeProp && (
    <span className={"scha__link__icon scha__link__icon--before"}>{iconBeforeProp}</span>
  );

  return (
    <button className={cls} {...btnProps} ref={ref}>
      {iconBefore}
      {children}
    </button>
  );
});

export type ArrowLinkProps = Omit<LinkProps, "iconBefore"> & { iconSize?: IconSize };

export const ArrowLink = forwardRef<HTMLAnchorElement, ArrowLinkProps>((props, ref) => {
  const { iconSize, ...rest } = props;
  return (
    <Link
      iconBefore={<Icon className={"scha__link--arrow-link"} type={IconType.ChevronRight} size={iconSize} />}
      ref={ref}
      {...rest}
    />
  );
});

export const BackLink = forwardRef<HTMLAnchorElement, ArrowLinkProps>((props, ref) => {
  const { iconSize, ...rest } = props;
  return (
    <Link
      iconBefore={<Icon className={"scha__link--arrow-link"} type={IconType.ChevronLeft} size={iconSize} />}
      ref={ref}
      {...rest}
    />
  );
});
